/**
 * Do-Not-Track System
 * 
 * Provides functionality to exclude internal traffic from tracking:
 * - URL-based opt-out (?do-not-track or /do-not-track)
 * - Automatic Lovable traffic detection
 * - Persistent localStorage preference
 * 
 * Usage:
 * - Visit any page with ?do-not-track to enable
 * - Visit any page with ?enable-tracking to disable opt-out
 * - Check isDoNotTrack() before sending any tracking events
 */

const DNT_STORAGE_KEY = 'kp_do_not_track';
const DNT_ENABLED_VALUE = 'enabled';

// Lovable domains and patterns to auto-exclude - comprehensive list
const LOVABLE_PATTERNS = [
  // Main domains
  'lovable.app',
  'lovable.dev',
  'lovable.io',
  'lovableproject.com',
  // Subdomains and patterns
  '.lovable.',
  '-lovable.',
  'lovable-',
  // URL tokens and params
  '__lovable',
  'lovable_token',
  // Preview patterns (catches fc2bf0ad-fa9c-4f95-96be-666212ad974d.lovableproject.com etc.)
  'id-preview--',
];

// Regex for UUID-based Lovable project URLs
const LOVABLE_UUID_PATTERN = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\.lovable/i;

/**
 * Check if current context is from Lovable (preview, editor, etc.)
 */
export function isLovableContext(): boolean {
  if (typeof window === 'undefined') return false;
  
  const hostname = window.location.hostname.toLowerCase();
  const href = window.location.href.toLowerCase();
  const referrer = document.referrer.toLowerCase();
  
  // Check hostname directly for Lovable domains
  if (hostname.includes('lovable') || hostname.includes('lovableproject')) {
    return true;
  }
  
  // Check URL and referrer for Lovable patterns
  for (const pattern of LOVABLE_PATTERNS) {
    if (href.includes(pattern) || referrer.includes(pattern)) {
      return true;
    }
  }
  
  // Check for UUID-based Lovable project URLs (e.g., fc2bf0ad-...-666212ad974d.lovableproject.com)
  if (LOVABLE_UUID_PATTERN.test(hostname) || LOVABLE_UUID_PATTERN.test(referrer)) {
    return true;
  }
  
  // Check for Lovable token in URL (used in preview)
  if (href.includes('__lovable_token') || href.includes('lovable_token')) {
    return true;
  }
  
  // Check if running in iframe from Lovable
  try {
    if (window.self !== window.top) {
      const parentUrl = document.referrer.toLowerCase();
      if (parentUrl.includes('lovable') || LOVABLE_UUID_PATTERN.test(parentUrl)) {
        return true;
      }
    }
  } catch {
    // Cross-origin iframe - could be Lovable, be conservative
  }
  
  return false;
}

/**
 * Check if Do-Not-Track is enabled (either manually or auto-detected)
 */
export function isDoNotTrack(): boolean {
  if (typeof window === 'undefined') return true;
  
  // Auto-exclude Lovable traffic
  if (isLovableContext()) {
    return true;
  }
  
  // Check localStorage preference
  return localStorage.getItem(DNT_STORAGE_KEY) === DNT_ENABLED_VALUE;
}

/**
 * Enable Do-Not-Track preference
 */
export function enableDoNotTrack(): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(DNT_STORAGE_KEY, DNT_ENABLED_VALUE);
  console.log('[DNT] Do-Not-Track enabled');
}

/**
 * Disable Do-Not-Track preference
 */
export function disableDoNotTrack(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(DNT_STORAGE_KEY);
  console.log('[DNT] Do-Not-Track disabled');
}

/**
 * Get current DNT status info
 */
export function getDNTStatus(): { 
  enabled: boolean; 
  reason: 'manual' | 'lovable' | 'none';
  details: string;
} {
  if (typeof window === 'undefined') {
    return { enabled: true, reason: 'none', details: 'SSR context' };
  }
  
  if (isLovableContext()) {
    return { enabled: true, reason: 'lovable', details: 'Lovable preview/editor detected' };
  }
  
  if (localStorage.getItem(DNT_STORAGE_KEY) === DNT_ENABLED_VALUE) {
    return { enabled: true, reason: 'manual', details: 'Manually opted out via URL' };
  }
  
  return { enabled: false, reason: 'none', details: 'Tracking active' };
}

/**
 * Process URL parameters for DNT control
 * Call this on app initialization
 */
export function processDoNotTrackURL(): void {
  if (typeof window === 'undefined') return;
  
  const params = new URLSearchParams(window.location.search);
  
  // Check for opt-out
  if (params.has('do-not-track') || params.has('dnt') || params.has('notrack')) {
    enableDoNotTrack();
    
    // Clean URL
    params.delete('do-not-track');
    params.delete('dnt');
    params.delete('notrack');
    
    const newUrl = params.toString() 
      ? `${window.location.pathname}?${params.toString()}` 
      : window.location.pathname;
    
    window.history.replaceState({}, '', newUrl);
  }
  
  // Check for opt-in (re-enable tracking)
  if (params.has('enable-tracking') || params.has('track')) {
    disableDoNotTrack();
    
    // Clean URL
    params.delete('enable-tracking');
    params.delete('track');
    
    const newUrl = params.toString() 
      ? `${window.location.pathname}?${params.toString()}` 
      : window.location.pathname;
    
    window.history.replaceState({}, '', newUrl);
  }
}

/**
 * Initialize DNT system
 * Call once at app startup
 */
export function initDoNotTrack(): void {
  processDoNotTrackURL();
  
  const status = getDNTStatus();
  if (status.enabled) {
    console.log(`[DNT] Tracking disabled: ${status.details}`);
  }
}
